import { useRouter } from "next/router";
import { useEffect } from "react";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../services/hooks/useTranslationI18";
import {
  setSelectedFranchise,
  sortActiveFranchisesByDistance,
} from "../../../services/redux/slices/configSlice";
import {
  LoadingActionEnum,
  decreasePendingOperations,
  increasePendingOperations,
  pushToLoadingStack,
  setAnimatedGlobalBannerState,
  setLoading,
} from "../../../services/redux/slices/globalComponentsToggleSlice";
import {
  setLocation,
  setSortedFranchises,
} from "../../../services/redux/slices/locationSlice";
import { PagesEnum } from "../../../utils/enums";
import {
  calculateDistanceBetweenTwoCoordinates,
  sortFranchise,
} from "../../../utils/help/location/locationHelp";
import { FranchiseWithDistance, LatLng } from "../../../utils/types";

const lodashClonedeep = require("lodash.clonedeep");

function AutoDetectLocation(props: { page?: PagesEnum }) {
  const { t } = useTranslationI18();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { franchise } = router.query;

  const enableLocTranslation = t("delivery:enable-location");
  const connectionTranslation = t("delivery:connection-timeout");

  const franchises = useAppSelector((state) => state.config.activeFranchises);
  const sortedFranchises = useAppSelector(
    (state) => state.location.sortedFranchises
  );
  const savedLocation = useAppSelector((state) => state.location.location);
  const clickDetected = useAppSelector((state) => state.location.clickDetected);
  const locationDetected = useAppSelector((state) => state.location.location);
  const restaurantConfig = useAppSelector(
    (state) => state.config.restaurantInfo
  )?.config;

  const getCurrentPosition = (tries: number = 0) => {
    if (locationDetected) {
      dispatch(sortActiveFranchisesByDistance(savedLocation!));
      dispatch(setSortedFranchises(sortedFranchises));

      if (router.isReady && !franchise && props.page === PagesEnum.Home) {
        if (!clickDetected && sortedFranchises && sortedFranchises.length > 0) {
          dispatch(setSelectedFranchise(sortedFranchises[0]));
        }
      }

      // dispatch(setClickDetected(false));
      return;
    }
    if (restaurantConfig?.loadingStateSolution) {
      dispatch(
        pushToLoadingStack({
          action: LoadingActionEnum.INCREASE,
          functionName: "AutoDetectLocation getCurrentPosition",
          date: new Date(),
        })
      );
      dispatch(increasePendingOperations());
    } else {
      dispatch(setLoading(true));
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => handleSuccess(pos),
      (err) => handleError(err, tries),
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      }
    );
  };

  const handleSuccess = (pos: GeolocationPosition) => {
    const { latitude, longitude } = pos.coords;
    const location = { lat: latitude, lng: longitude };
    if (restaurantConfig?.loadingStateSolution) {
      dispatch(
        pushToLoadingStack({
          action: LoadingActionEnum.DECREASE,
          functionName: "AutoDetectLocation getCurrentPosition success",
          date: new Date(),
        })
      );
      dispatch(decreasePendingOperations());
    } else {
      dispatch(setLoading(false));
    }
    dispatch(setLocation(location));

    dispatch(sortActiveFranchisesByDistance(location));

    let clonedFranchises: FranchiseWithDistance[] = lodashClonedeep(franchises);

    for (let index = 0; index < clonedFranchises.length; index++) {
      let currentFranchiseLocation: LatLng | undefined;

      if (
        clonedFranchises[index]?.location &&
        clonedFranchises[index].location?.lat &&
        clonedFranchises[index].location?.lng
      ) {
        currentFranchiseLocation = clonedFranchises[index].location;
      }

      const distanceFromLocationInMeters = currentFranchiseLocation
        ? calculateDistanceBetweenTwoCoordinates(
            location,
            currentFranchiseLocation
          )
        : undefined;
      clonedFranchises[index] = {
        ...clonedFranchises[index],
        distanceFromLocationInMeters,
      };
    }

    clonedFranchises = clonedFranchises.sort((a, b) => sortFranchise(a, b));
    dispatch(setSortedFranchises(clonedFranchises));

    if (router.isReady && !franchise && props.page === PagesEnum.Home) {
      if (
        clonedFranchises &&
        clonedFranchises.length > 0 &&
        clonedFranchises[0]?.id
      ) {
        dispatch(setSelectedFranchise(clonedFranchises[0]));
      }
    }
  };

  const handleError = (err: GeolocationPositionError, tries: number) => {
    if (tries < 2) {
      return getCurrentPosition(tries + 1);
    }

    let errorMessage;
    switch (err.code) {
      case 1:
        errorMessage = enableLocTranslation;
        break;
      case 3:
        errorMessage = connectionTranslation;
        break;
      default:
        errorMessage = t("common:something-went-wrong");
        break;
    }

    if (restaurantConfig?.loadingStateSolution) {
      dispatch(
        pushToLoadingStack({
          action: LoadingActionEnum.DECREASE,
          functionName: "AutoDetectLocation getCurrentPosition error",
          date: new Date(),
        })
      );
      dispatch(decreasePendingOperations());
    } else {
      dispatch(setLoading(false));
    }
    dispatch(
      setAnimatedGlobalBannerState({
        isOpen: true,
        text: errorMessage,
        warning: false,
        info: true,
      })
    );

    console.warn(`ERROR(${err.code}): ${err.message}`);
  };

  useEffect(() => {
    getCurrentPosition(0);
  }, [router.isReady]);

  return null;
}

export default AutoDetectLocation;
